import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { ChevronDown, Check } from '../icons/Icons';

const locales = {
  am: {
    name: 'Հայերեն',
    short: 'ՀԱՅ',
    image: `${process.env.PUBLIC_URL}/assets/images/flags/armenia.png`,
  },
  en: {
    name: 'English',
    short: 'ENG',
    image: `${process.env.PUBLIC_URL}/assets/images/flags/uk.png`,
  },
  ru: {
    name: 'Русский',
    short: 'РУС',
    image: `${process.env.PUBLIC_URL}/assets/images/flags/russia.png`,
  },
};
const cookies = new Cookies();

export type Locale = 'am' | 'ru' | 'en';

export const getLocale = (defaultLocale: Locale) => {
  if (window) {
    const storegeLocal = defaultLocale || cookies.get('locale');

    if (storegeLocal && storegeLocal in locales) {
      const key: Locale = storegeLocal;

      return locales[key];
    }
  }

  return locales.am;
};

type PropsType = {
  /** Rendered on the dark utility strip by default; `light` suits the drawer. */
  tone?: 'dark' | 'light';
};

const LanguagePicker = ({ tone = 'dark' }: PropsType) => {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultLocale = searchParams.get('locale');
  const locale = useRef<Locale>('am');
  const wrapper = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    let siteLocale = 'am';
    if (window) {
      const storegeLocal = defaultLocale || cookies.get('locale');

      if (storegeLocal && storegeLocal in locales) {
        siteLocale = storegeLocal;
      }
    }

    locale.current = siteLocale as Locale;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapper.current && !wrapper.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    if (defaultLocale) {
      setSearchParams({ locale: '' });
    }

    cookies.set('locale', lang, {
      path: '/',
      expires: new Date(Date.now() + 60 * 86400000),
    });

    window.location.reload();
  };

  const current = locales[locale.current];

  return (
    <div ref={wrapper} className='relative'>
      <button
        onClick={() => setOpen(!open)}
        type='button'
        aria-expanded={open}
        aria-label={current.name}
        className={`flex items-center gap-2 rounded-pill px-2 py-1 text-[12px] font-medium transition-colors ${
          tone === 'dark'
            ? 'text-ink-200 hover:bg-ink-800 hover:text-paper'
            : 'text-ink-700 hover:bg-canvas'
        }`}
      >
        <img src={current.image} width={16} height={16} alt='' className='rounded-sm' />
        <span>{current.name}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className='menu-panel absolute right-0 top-[calc(100%+8px)] z-[60] w-[168px]'>
          {(Object.keys(locales) as Locale[]).map((key) => (
            <button
              key={key}
              type='button'
              onClick={() => changeLanguage(key)}
              className='menu-item'
            >
              <img src={locales[key].image} width={16} height={16} alt='' className='rounded-sm' />
              <span className='flex-1'>{locales[key].name}</span>
              {key === locale.current && <Check size={15} className='text-apricot-600' />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguagePicker;
